import React, { useState, useEffect } from 'react';
import { ref, get, child, getDatabase } from 'firebase/database';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/admin/AdminLayout';
import './StatisticsDashboard.css';

function StatisticsDashboard() {
  const { currentUser, isAdmin } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Statistics state
  const [totalStats, setTotalStats] = useState(0);
  const [componentStats, setComponentStats] = useState({ ROTC: 0, CWTS: 0 });
  const [genderStats, setGenderStats] = useState({ male: 0, female: 0, other: 0 });
  const [courseStats, setCourseStats] = useState({});
  const [mostPopularCourse, setMostPopularCourse] = useState({ name: '', count: 0 });
  const [yearLevelStats, setYearLevelStats] = useState({});

  // UI state
  const [activeFilter, setActiveFilter] = useState('all');
  const [timePeriod, setTimePeriod] = useState('all-time');
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Fetch all students from the database
  useEffect(() => {
    const fetchStudents = async () => {
      setLoading(true);
      setError('');
      
      // Check if user is authenticated
      if (!currentUser) {
        setError('Authentication required. Please login.');
        setLoading(false);
        return;
      }
      
      try {
        console.log('Fetching students data...');
        const dbRef = ref(getDatabase());
        const snapshot = await get(child(dbRef, 'students'));
        
        if (snapshot.exists()) {
          console.log('Students data found');
          const studentsData = snapshot.val();
          
          // Convert object to array
          const studentsArray = Object.keys(studentsData).map(key => ({
            id: key,
            ...studentsData[key]
          }));
          
          console.log(`Loaded ${studentsArray.length} students`);
          setStudents(studentsArray);
          
          // Calculate statistics
          calculateAllStats(studentsArray);
          setError('');
        } else {
          console.log('No students data found');
          setStudents([]);
          resetStats();
          setError('');
        }
      } catch (err) {
        console.error('Error fetching students:', err);
        
        if (err.message.includes('permission_denied') || err.message.includes('Permission denied')) {
          setError('Permission denied. Please check your access privileges.');
        } else {
          setError('Failed to load students data. Error: ' + err.message);
        }
        
        setStudents([]);
        resetStats();
      } finally {
        setLoading(false);
        setTimeout(() => {
          setIsInitialLoad(false);
        }, 500);
      }
    };

    fetchStudents();
  }, [currentUser]);

  // Reset all statistics
  const resetStats = () => {
    setTotalStats(0);
    setComponentStats({ ROTC: 0, CWTS: 0 });
    setGenderStats({ male: 0, female: 0, other: 0 });
    setCourseStats({});
    setMostPopularCourse({ name: '', count: 0 });
    setYearLevelStats({});
  };

  // Calculate all statistics
  const calculateAllStats = (studentsArray) => {
    // Set total students
    setTotalStats(studentsArray.length);
    
    // Calculate component statistics
    const componentCounts = { ROTC: 0, CWTS: 0 };
    
    // Calculate gender statistics
    const genderCounts = { male: 0, female: 0, other: 0 };
    
    // Calculate course statistics
    const courseCounts = {};
    
    // Calculate year level statistics
    const yearLevelCounts = { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0, 'Others': 0 };
    
    studentsArray.forEach(student => {
      // Component stats
      const component = student.nstpComponent;
      if (component === 'ROTC') {
        componentCounts.ROTC += 1;
      } else if (component === 'CWTS') {
        componentCounts.CWTS += 1;
      }
      
      // Gender stats
      const gender = (student.sex || '').toLowerCase();
      if (gender === 'male') {
        genderCounts.male += 1;
      } else if (gender === 'female') {
        genderCounts.female += 1;
      } else {
        genderCounts.other += 1;
      }
      
      // Course stats
      const course = student.course || 'Unknown';
      if (courseCounts[course]) {
        courseCounts[course] += 1;
      } else {
        courseCounts[course] = 1;
      }
      
      // Year level stats
      const yearLevel = student.yearLevel || 'Others';
      if (yearLevel >= 1 && yearLevel <= 5) {
        yearLevelCounts[yearLevel] += 1;
      } else {
        yearLevelCounts['Others'] += 1;
      }
    });
    
    // Set all statistics
    setComponentStats(componentCounts);
    setGenderStats(genderCounts);
    setCourseStats(courseCounts);
    setYearLevelStats(yearLevelCounts);
    
    // Find most popular course
    let maxCount = 0;
    let popularCourse = '';
    Object.entries(courseCounts).forEach(([course, count]) => {
      if (count > maxCount) {
        maxCount = count;
        popularCourse = course;
      }
    });
    
    setMostPopularCourse({
      name: popularCourse,
      count: maxCount
    });
  };

  // Calculate component percentage
  const calculatePercentage = (value, total) => {
    if (total === 0) return 0;
    return ((value / total) * 100).toFixed(1);
  };

  // Handle print functionality
  const handlePrint = () => {
    window.print();
  };

  // Handle export functionality (now with feedback)
  const handleExport = () => {
    // Create a feedback element
    const feedbackEl = document.createElement('div');
    feedbackEl.className = 'export-feedback';
    feedbackEl.innerHTML = '<span>⬇️ Preparing Excel export...</span>';
    document.body.appendChild(feedbackEl);
    
    // Simulate export process
    setTimeout(() => {
      feedbackEl.innerHTML = '<span>✅ Export complete!</span>';
      
      // Remove the feedback after showing success
      setTimeout(() => {
        document.body.removeChild(feedbackEl);
      }, 2000);
    }, 1500);
  };

  // Filter based on time period (implementation simplified)
  const getFilteredStats = () => {
    return {
      total: totalStats,
      component: componentStats,
      gender: genderStats
    };
  };
  
  const stats = getFilteredStats();

  if (loading) {
    return (
      <AdminLayout>
        <div className="statistics-container loading">
          <div className="loading-animation">
            <div className="spinner"></div>
            <p>Loading statistics...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="statistics-container">
        <div className="dashboard-header">
          <div className="header-content">
            <h1>NSTP Statistics Dashboard</h1>
            <p className="dashboard-description">Comprehensive overview of student registrations and program metrics</p>
          </div>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        <div className="statistics-menu">
          <div className="menu-left">
            <div className="stat-period-selector">
              <label>Time Period:</label>
              <select 
                value={timePeriod} 
                onChange={(e) => setTimePeriod(e.target.value)}
                className="period-select"
              >
                <option value="all-time">All Time</option>
                <option value="current-semester">Current Semester</option>
                <option value="last-semester">Last Semester</option>
                <option value="current-year">Current Year</option>
              </select>
            </div>
            <div className="stat-filter-buttons">
              <button 
                className={`stat-filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
                onClick={() => setActiveFilter('all')}
              >
                All
              </button>
              <button 
                className={`stat-filter-btn ${activeFilter === 'ROTC' ? 'active' : ''}`}
                onClick={() => setActiveFilter('ROTC')}
              >
                ROTC
              </button>
              <button 
                className={`stat-filter-btn ${activeFilter === 'CWTS' ? 'active' : ''}`}
                onClick={() => setActiveFilter('CWTS')}
              >
                CWTS
              </button>
            </div>
          </div>
          <div className="menu-right">
            <button className="stat-action-btn export-btn" onClick={handleExport}>
              <i className="icon">📊</i> Export
            </button>
            <button className="stat-action-btn print-btn" onClick={handlePrint}>
              <i className="icon">🖨️</i> Print
            </button>
          </div>
        </div>
        
        <div className={`dashboard-content ${isInitialLoad ? 'initial-load' : ''}`}>
          {/* Summary Cards Section */}
          <div className="summary-cards">
            <div className="stat-card total-students-card">
              <div className="stat-content">
                <h3>Total Students</h3>
                <div className="stat-value">{stats.total}</div>
                <div className="stat-change">
                  <span className="trend-indicator">
                    <i className="trend-icon">⟩</i>
                  </span>
                  <span>+0 from last period</span>
                </div>
              </div>
              <div className="stat-circle">
                {stats.total}
              </div>
            </div>
            
            <div className="stat-card component-card rotc-card">
              <div className="component-icon-wrapper">
                <div className="component-icon">🪖</div>
              </div>
              <div className="stat-content">
                <h3>ROTC</h3>
                <div className="stat-value">{stats.component.ROTC}</div>
                <div className="stat-percentage">{calculatePercentage(stats.component.ROTC, stats.total)}% of total</div>
              </div>
            </div>
            
            <div className="stat-card component-card cwts-card">
              <div className="component-icon-wrapper">
                <div className="component-icon">🤝</div>
              </div>
              <div className="stat-content">
                <h3>CWTS</h3>
                <div className="stat-value">{stats.component.CWTS}</div>
                <div className="stat-percentage">{calculatePercentage(stats.component.CWTS, stats.total)}% of total</div>
              </div>
            </div>
          </div>
          
          {/* Charts Section */}
          <div className="statistics-charts">
            <div className="chart-container component-distribution">
              <h3>NSTP Component Distribution</h3>
              <div className="component-distribution-content">
                <div className="pie-chart-container">
                  <div className="pie-chart">
                    {/* Empty state if no data */}
                    {stats.total === 0 ? (
                      <div className="no-data-overlay">
                        <div className="no-data-message">No data available</div>
                      </div>
                    ) : (
                      <>
                        {stats.component.ROTC > 0 && (
                          <div 
                            className="pie-segment rotc" 
                            style={{
                              clipPath: `polygon(50% 50%, 50% 0%, ${50 + 50 * calculatePercentage(stats.component.ROTC, stats.total) / 100}% 0%, 100% 100%)`,
                              zIndex: 3
                            }}
                          ></div>
                        )}
                        {stats.component.CWTS > 0 && (
                          <div 
                            className="pie-segment cwts" 
                            style={{
                              transform: `rotate(${3.6 * calculatePercentage(stats.component.ROTC, stats.total)}deg)`,
                              clipPath: `polygon(50% 50%, 50% 0%, ${50 + 50 * calculatePercentage(stats.component.CWTS, stats.total) / 100}% 0%, 100% 100%)`,
                              zIndex: 2
                            }}
                          ></div>
                        )}
                      </>
                    )}
                    <div className="pie-center">
                      <div className="pie-total">{stats.total}</div>
                      <div className="pie-label">students</div>
                    </div>
                  </div>
                </div>
                <div className="distribution-legend">
                  <div className="legend-item">
                    <div className="legend-color rotc"></div>
                    <div className="legend-label">ROTC:</div>
                    <div className="legend-value">{stats.component.ROTC}</div>
                    <div className="legend-percentage">({calculatePercentage(stats.component.ROTC, stats.total)}%)</div>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color cwts"></div>
                    <div className="legend-label">CWTS:</div>
                    <div className="legend-value">{stats.component.CWTS}</div>
                    <div className="legend-percentage">({calculatePercentage(stats.component.CWTS, stats.total)}%)</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="chart-container gender-distribution">
              <h3>Gender Distribution</h3>
              <div className="gender-chart">
                <div className="bar-chart">
                  <div className="bar-item">
                    <div className="bar-label">Male</div>
                    <div className="bar-outer">
                      <div 
                        className="bar-inner male" 
                        style={{ width: `${calculatePercentage(stats.gender.male, stats.total)}%` }}
                      >
                        {stats.gender.male > 0 && (
                          <span className="bar-value">{stats.gender.male}</span>
                        )}
                      </div>
                    </div>
                    <div className="bar-percentage">{calculatePercentage(stats.gender.male, stats.total)}%</div>
                  </div>
                  
                  <div className="bar-item">
                    <div className="bar-label">Female</div>
                    <div className="bar-outer">
                      <div 
                        className="bar-inner female" 
                        style={{ width: `${calculatePercentage(stats.gender.female, stats.total)}%` }}
                      >
                        {stats.gender.female > 0 && (
                          <span className="bar-value">{stats.gender.female}</span>
                        )}
                      </div>
                    </div>
                    <div className="bar-percentage">{calculatePercentage(stats.gender.female, stats.total)}%</div>
                  </div>
                  
                  <div className="bar-item">
                    <div className="bar-label">Other</div>
                    <div className="bar-outer">
                      <div 
                        className="bar-inner other" 
                        style={{ width: `${calculatePercentage(stats.gender.other, stats.total)}%` }}
                      >
                        {stats.gender.other > 0 && (
                          <span className="bar-value">{stats.gender.other}</span>
                        )}
                      </div>
                    </div>
                    <div className="bar-percentage">{calculatePercentage(stats.gender.other, stats.total)}%</div>
                  </div>
                </div>
                
                <div className="gender-summary">
                  <div className="gender-ratio">
                    <div className="gender-label">Ratio (M:F):</div>
                    <div className="ratio-value">
                      {stats.gender.male || 0} : {stats.gender.female || 0}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Statistics Details Section */}
          <div className="statistics-details">
            <div className="chart-container popular-courses">
              <h3>Popular Courses</h3>
              <div className="courses-list">
                {Object.entries(courseStats).length === 0 ? (
                  <div className="no-data-message">No course data available</div>
                ) : (
                  Object.entries(courseStats)
                    .sort(([, countA], [, countB]) => countB - countA)
                    .slice(0, 5)
                    .map(([course, count], index) => (
                      <div key={index} className="course-item">
                        <div className="course-rank">{index + 1}</div>
                        <div className="course-info">
                          <div className="course-name">{course}</div>
                          <div className="course-bar-container">
                            <div 
                              className="course-bar" 
                              style={{ width: `${count / (mostPopularCourse.count || 1) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                        <div className="course-stats">
                          <span className="course-count">{count}</span>
                          <span className="course-percent">({calculatePercentage(count, stats.total)}%)</span>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
            
            <div className="chart-container year-level-distribution">
              <h3>Year Level Distribution</h3>
              <div className="year-level-chart">
                {Object.entries(yearLevelStats).length === 0 ? (
                  <div className="no-data-message">No year level data available</div>
                ) : (
                  Object.entries(yearLevelStats).map(([year, count]) => (
                    <div key={year} className="year-level-item">
                      <div className="year-label">Year {year}</div>
                      <div className="year-bar-container">
                        <div 
                          className={`year-bar year-${year}`}
                          style={{ width: `${calculatePercentage(count, stats.total)}%` }}
                        >
                          {count > 0 && <span className="year-count">{count}</span>}
                        </div>
                      </div>
                      <div className="year-percentage">{calculatePercentage(count, stats.total)}%</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default StatisticsDashboard; 